import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BrainCog, 
  Network, 
  ExternalLink, 
  ChevronRight, 
  Mail, 
  Award,
  Linkedin,
  BookIcon,
  ScrollIcon,
  Zap,
  Activity,
  ShieldCheck,
  Globe
} from 'lucide-react';
import InfrastructureDiagram from '../components/InfrastructureDiagram';
import './Home.css';

function Home() {
  // Research pillars defining the core scientific vision
  const researchPillars = [
    {
      title: 'Pillar I: Micro-Scale AI Efficiency & Dynamics',
      tagline: 'Georgia Tech PhD Foundation',
      description: (
        <>
          Designing hyper-efficient neural architectures using <strong>spiking neural networks (SNNs), state-space models (SSMs), and dynamic graph attention</strong> to reduce memory and compute overhead by orders of magnitude.
        </>
      ),
      icon: BrainCog,
      color: 'theme-purple',
      link: '/research-statement'
    },
    {
      title: 'Pillar II: Macro-Scale Energy & Market Intelligence',
      tagline: 'Mercuria Energy AI Frontier',
      description: (
        <>
          Building predictive AI systems that model complex spatio-temporal dynamics across <strong>power grids and natural gas pipeline networks</strong> supplying energy for hyper-scale compute.
        </>
      ),
      icon: Zap,
      color: 'theme-amber',
      link: '/energy-systems'
    },
    {
      title: 'Pillar III: Closed-Loop Infrastructure Optimization',
      tagline: 'Unified Research Agenda',
      description: (
        <>
          Developing autonomous control frameworks that <strong>co-optimize distributed AI workload dispatch</strong> with regional energy grid constraints, pricing signals, and renewable intermittency.
        </>
      ),
      icon: Network,
      color: 'theme-blue',
      link: '/future-agenda'
    }
  ];

  const quickLinks = [
    {
      title: 'Publications',
      icon: BookIcon,
      link: '/publications',
      isExternal: false,
    },
    {
      title: 'PhD Research',
      icon: ScrollIcon,
      link: '/research-statement',
      isExternal: false,
    },
    {
      title: 'Energy & Infrastructure',
      icon: Zap,
      link: '/energy-systems',
      isExternal: false,
    },
    {
      title: 'Future Agenda',
      icon: Globe,
      link: '/future-agenda',
      isExternal: false,
    }
  ];

  // Selected honors & key milestones (Top items only for clean homepage display)
  const news = [
    {
      date: 'March 2026',
      content: 'Awarded the 2026 Georgia Tech Sigma Xi Best Ph.D. Thesis Award for research on efficient adaptive neural systems.',
      type: 'award',
      link: 'https://bpb-us-e1.wpmucdn.com/sites.gatech.edu/dist/0/283/files/2026/03/2026_SigmaXi_Research_Award_Winner.pdf'
    },
    {
      date: 'September 2025',
      content: 'Paper accepted at NeurIPS 2025: "FLAME: Fast Long-context Adaptive Memory for Event-based Vision".',
      type: 'publication',
      link: 'https://nips.cc/v2/Conferences/2025'
    },
    {
      date: 'May 2025',
      content: 'Joined Mercuria Energy America as Quantitative Researcher / AI Scientist.',
      type: 'career'
    },
    {
      date: 'May 2025',
      content: 'Paper accepted at ICML 2025: "A Dynamical Systems-Inspired Pruning Strategy for Graph Attention Networks".',
      type: 'publication',
      link: 'https://icml.cc/v2/Conferences/2025'
    },
    {
      date: 'July 2024',
      content: 'Received the MLCommons Rising Stars Award 2024 for contributions to efficient AI.',
      type: 'award',
      link: 'https://mlcommons.org/2024/06/2024-mlc-rising-stars/'
    }
  ];

  // Add the ClustrMaps script dynamically
  useEffect(() => {
    const mapContainer = document.getElementById('map-container');
    if (mapContainer && !document.getElementById('clustrmaps')) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.id = 'clustrmaps';
      script.src = '//cdn.clustrmaps.com/map_v2.js?cl=080808&w=300&t=n&d=HVEBTibQBKqWi1hsx9cxWf9yauuHfJgjvQkVIzDjrfE&co=ffffff&cmo=3acc3a&cmn=ff5353&ct=808080';
      script.async = true;
      mapContainer.appendChild(script);
    }
  }, []);

  return (
    <div className="home">
      {/* Hero Header Section */}
      <header className="home-header">
        <div className="profile-container glass">
          <img src="profile.png" alt="Biswadeep Chakraborty" className="profile-pic" />
          <div className="header-content">
            <span className="hero-badge">AI Scientist & Quantitative Researcher</span>
            <h1>Biswadeep Chakraborty</h1>
            <p className="hero-vision-title">
              Building the Intelligence Layer for Critical Infrastructure
            </p>
            <p className="hero-bio-summary">
              AI Scientist at <strong>Mercuria Energy</strong> • Ph.D. in AI from <strong>Georgia Tech</strong>.  
              Bridging fundamental neural architectures—spiking networks, state-space models, and dynamical systems—with real-time optimization of power grids, natural gas markets, and energy infrastructure.
            </p>

            <div className="header-links">
              <a
                href="https://scholar.google.com/citations?user=8soIjY8AAAAJ&hl=en&authuser=1"
                target="_blank"
                rel="noopener noreferrer"
                className="header-link"
              >
                <Award className="link-icon" />
                Google Scholar
              </a>
              <span className="divider">•</span>
              <a
                href="https://www.linkedin.com/in/biswadeepc/"
                target="_blank"
                rel="noopener noreferrer"
                className="header-link"
              >
                <Linkedin className="link-icon" />
                LinkedIn
              </a>
              <span className="divider">•</span>
              <a href="mailto:biswadeep@gatech.edu" className="header-link">
                <Mail className="link-icon" />
                Email
              </a>
            </div>
          </div>
        </div>

        {/* Quick Nav Links */}
        <div className="quick-links">
          {quickLinks.map((link, index) => {
            const IconComponent = link.icon;
            return (
              <Link key={index} to={link.link} className="quick-link-button glass">
                <IconComponent className="quick-link-icon" />
                {link.title}
              </Link>
            );
          })}
        </div>
      </header>

      {/* Central Manifesto Section */}
      <section className="manifesto-section glass">
        <div className="manifesto-inner">
          <div className="manifesto-tag">Central Research Thesis</div>
          <h2>Artificial Intelligence Cannot Exist Without Physical Infrastructure</h2>
          <p className="manifesto-text">
            For the past decade, AI scaling focused primarily on expanding model parameters and static dataset sizes. 
            However, the fundamental bottleneck has now shifted to physical reality: <strong>electricity generation, regional grid stability, natural gas deliverability, thermal dissipation, and supply chain constraints</strong>.
          </p>
          <p className="manifesto-text">
            My research agenda focuses on architecting the <strong>intelligence layer</strong>—spatio-temporal models, adaptive state-space representations, and closed-loop control systems—that makes physical energy and compute infrastructure intelligent, resilient, and self-optimizing.
          </p>
        </div>
      </section>

      {/* System Architecture Diagram */}
      <InfrastructureDiagram />

      {/* Three Research Pillars */}
      <section className="themes-section">
        <div className="section-header-center">
          <span className="section-kicker">Unified Framework</span>
          <h2>Research Pillars & Core Focus</h2>
        </div>
        <div className="themes-grid">
          {researchPillars.map((pillar, index) => {
            const IconComponent = pillar.icon;
            return (
              <div key={index} className={`theme-card glass glass-hover ${pillar.color}`}>
                <div className="theme-header">
                  <div className="icon-container">
                    <IconComponent className="theme-icon" />
                  </div>
                  <div>
                    <span className="pillar-tagline">{pillar.tagline}</span>
                    <h3>{pillar.title}</h3>
                  </div>
                </div>
                <p>{pillar.description}</p>
                <Link to={pillar.link} className="pillar-link">
                  Explore Research <ChevronRight className="link-arrow" />
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* Mercuria Energy Feature Spotlight */}
      <section className="mercuria-spotlight glass">
        <div className="spotlight-content">
          <div className="spotlight-badge">
            <Zap className="badge-icon" />
            Current Focus at Mercuria Energy
          </div>
          <h2>AI Systems for Power & Natural Gas Markets</h2>
          <p>
            At Mercuria, I develop production-grade AI systems modeling complex spatio-temporal dynamics across physical energy markets. This work directly addresses surging power demand driven by hyper-scale AI data centers, renewable transition volatility, and regional grid congestion.
          </p>
          <div className="spotlight-bullets">
            <div className="bullet-item">
              <Activity className="bullet-icon" />
              <span><strong>Power Grid Load & Congestion:</strong> Multi-node spatio-temporal forecasting for regional electricity grids.</span>
            </div>
            <div className="bullet-item">
              <ShieldCheck className="bullet-icon" />
              <span><strong>Natural Gas Flow Dynamics:</strong> Pipeline deliverability and storage optimization under peak demand events.</span>
            </div>
          </div>
          <div className="spotlight-cta">
            <Link to="/energy-systems" className="cta-button">
              Explore Energy Systems AI <ChevronRight className="cta-icon" />
            </Link>
          </div>
        </div>
      </section>

      {/* Selected Honors & Recognition Section */}
      <section className="news-section">
        <div className="section-header-center">
          <span className="section-kicker">Milestones</span>
          <h2>Selected Honors & Recognition</h2>
        </div>
        <div className="news-container">
          {news.map((item, index) => (
            <div key={index} className="news-item glass glass-hover" data-type={item.type}>
              <span className="news-date">{item.date}</span>
              <div className="news-content">
                <p>{item.content}</p>
                {item.link && (
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="news-link"
                  >
                    Read Link <ExternalLink className="link-icon-small" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="news-view-all">
          <Link to="/news" className="view-all-button">
            View All Updates & Recognition <ChevronRight className="button-icon" />
          </Link>
        </div>
      </section>

      {/* Visitor Map Section */}
      <section className="map-section">
        <h2>Global Research Reach</h2>
        <div id="map-container"></div>
      </section>
    </div>
  );
}

export default Home;